import { Fragment, useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiError, qs, useAction, useApi } from '../lib/api';
import { days, planPrice, useBilling, useManageBilling, useSubscribe } from '../lib/billing';
import { toast } from '../lib/toast';
import { date, dateTime, money, parseAmount, relative, titleCase, toInput, todayIso } from '../lib/format';
import { useSession } from '../lib/session';
import type { Access, ApiKey, AuditRow, Billing, ConnectedApp, CreatedApiKey, CreatedInvite, Invite, Members, Role, Webhook, WebhookDelivery, Workflow } from '../lib/types';
import { useActorLabel } from '../components/entry';
import { sessionHint } from '../site/hooks';
import { Icons } from '../components/icons';
import { Avatar, Badge, Button, Card, cx, Empty, ErrorNote, Field, Input, Modal, MoneyInput, PageHeader, Progress, Query, Select, SourceBadge, StatusBadge, Table, Tabs, Td, Th, Toggle } from '../components/ui';

type Tab = 'company' | 'rates' | 'members' | 'billing' | 'developers' | 'webhooks' | 'workflows' | 'audit' | 'account';

export function SettingsPage() {
  const [params, setParams] = useSearchParams();
  const tab = (params.get('tab') as Tab) ?? 'company';
  return (
    <>
      <PageHeader title="Settings" description="Company preferences, people, integrations and the audit trail." />
      <Tabs
        className="mb-5"
        value={tab}
        onChange={(t) => setParams({ tab: t }, { replace: true })}
        items={[
          { key: 'company', label: 'Company' },
          { key: 'rates', label: 'Exchange rates' },
          { key: 'members', label: 'Members' },
          { key: 'billing', label: 'Billing' },
          { key: 'developers', label: 'API & MCP' },
          { key: 'webhooks', label: 'Webhooks' },
          { key: 'workflows', label: 'Approval workflows' },
          { key: 'audit', label: 'Audit log' },
          { key: 'account', label: 'Your account' },
        ]}
      />
      {tab === 'company' && <CompanyTab />}
      {tab === 'rates' && <RatesTab />}
      {tab === 'members' && <MembersTab />}
      {tab === 'billing' && <BillingTab />}
      {tab === 'developers' && <DevelopersTab />}
      {tab === 'webhooks' && <WebhooksTab />}
      {tab === 'workflows' && <WorkflowsTab />}
      {tab === 'audit' && <AuditTab />}
      {tab === 'account' && <AccountTab />}
    </>
  );
}

function AdminOnly({ children }: { children: ReactNode }) {
  const { can } = useSession();
  if (can('ADMIN')) return <>{children}</>;
  return <p className="mb-4 rounded-md bg-sunken px-3 py-2 text-[12.5px] text-muted">Only admins and owners can change these settings.</p>;
}

// --- Company ------------------------------------------------------------------------------------------------------

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

function CompanyTab() {
  const { org, currency, can } = useSession();
  const s = org.settings;
  const [f, setF] = useState(() => ({
    name: org.name,
    industry: s.industry ?? '',
    fiscalYearStartMonth: s.fiscalYearStartMonth,
    materiality: toInput(s.materiality, currency),
    autoMatchThreshold: s.autoMatchThreshold,
    autopilot: s.autopilot,
    allowSelfApproval: !!s.allowSelfApproval,
  }));
  useEffect(() => {
    setF({ name: org.name, industry: s.industry ?? '', fiscalYearStartMonth: s.fiscalYearStartMonth, materiality: toInput(s.materiality, currency), autoMatchThreshold: s.autoMatchThreshold, autopilot: s.autopilot, allowSelfApproval: !!s.allowSelfApproval });
  }, [org, s, currency]);
  const materiality = parseAmount(f.materiality, currency);
  const save = useAction(
    () =>
      api('/api/org', {
        method: 'PATCH',
        body: { name: f.name, industry: f.industry || undefined, fiscalYearStartMonth: f.fiscalYearStartMonth, materiality: materiality ?? undefined, autoMatchThreshold: f.autoMatchThreshold, autopilot: f.autopilot, allowSelfApproval: f.allowSelfApproval },
      }),
    { success: 'Settings saved' },
  );
  const disabled = !can('ADMIN');
  return (
    <div className="grid max-w-5xl gap-5 lg:grid-cols-2">
      <Card title="Company">
        <AdminOnly>{null}</AdminOnly>
        <div className="space-y-3">
          <Field label="Legal name">
            <Input value={f.name} disabled={disabled} onChange={(e) => setF({ ...f, name: e.target.value })} />
          </Field>
          <Field label="Industry">
            <Input value={f.industry} disabled={disabled} onChange={(e) => setF({ ...f, industry: e.target.value })} placeholder="e.g. B2B SaaS" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Base currency" hint="Set when the company is created">
              <Input value={org.baseCurrency} disabled />
            </Field>
            <Field label="Fiscal year starts">
              <Select value={f.fiscalYearStartMonth} disabled={disabled} onChange={(e) => setF({ ...f, fiscalYearStartMonth: Number(e.target.value) })}>
                {MONTHS.map((m, i) => (
                  <option key={m} value={i + 1}>
                    {m}
                  </option>
                ))}
              </Select>
            </Field>
          </div>
        </div>
      </Card>
      <Card title="Automation and controls">
        <div className="space-y-4">
          <Field label="Materiality threshold" hint="Flux variances and manual entries above this are flagged for review." error={f.materiality && materiality === null ? 'Enter an amount' : undefined}>
            <MoneyInput currency={currency} value={f.materiality} disabled={disabled} onChange={(e) => setF({ ...f, materiality: e.target.value })} />
          </Field>
          <Field label={`Auto-match confidence: ${f.autoMatchThreshold}`} hint="Bank matches at or above this score are confirmed automatically when autopilot is on.">
            <input type="range" min={50} max={100} value={f.autoMatchThreshold} disabled={disabled} onChange={(e) => setF({ ...f, autoMatchThreshold: Number(e.target.value) })} className="w-full accent-[var(--color-brand)]" />
          </Field>
          <Toggle checked={f.autopilot} disabled={disabled} onChange={(v) => setF({ ...f, autopilot: v })} label="Reconciliation autopilot" description="Run auto-matching in the background as bank transactions arrive." />
          <Toggle
            checked={f.allowSelfApproval}
            disabled={disabled}
            onChange={(v) => setF({ ...f, allowSelfApproval: v })}
            label="Allow self-approval"
            description="For one-person finance teams. Leave off to keep segregation of duties: requesters cannot approve their own entries."
          />
        </div>
      </Card>
      <div className="flex items-center justify-end gap-3 lg:col-span-2">
        {save.error && <ErrorNote error={save.error} />}
        <Button variant="primary" disabled={disabled || !f.name.trim() || (!!f.materiality && materiality === null)} loading={save.isPending} onClick={() => save.mutate()}>
          Save changes
        </Button>
      </div>
      {can('ADMIN') && <YourDataCard />}
      {can('OWNER') && <DeleteCompanyCard />}
    </div>
  );
}

/** Everything the company has, as one JSON file. Fetched first so a failure shows as a message, not a broken download. */
function YourDataCard() {
  const [busy, setBusy] = useState(false);
  async function download() {
    setBusy(true);
    try {
      const res = await fetch('/api/org/export', { credentials: 'same-origin' });
      if (!res.ok) throw new Error(((await res.json().catch(() => null)) as { detail?: string } | null)?.detail ?? `Export failed (${res.status})`);
      const name = /filename="([^"]+)"/.exec(res.headers.get('content-disposition') ?? '')?.[1] ?? 'raremargin-export.json';
      const url = URL.createObjectURL(await res.blob());
      const a = document.createElement('a');
      a.href = url;
      a.download = name;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <Card title="Your data" subtitle="Every entry, invoice, bill, contract, bank line, rule and the audit log, as one JSON file." className="lg:col-span-2">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="max-w-xl text-[13px] text-ink-2">Keep a copy, or take your books elsewhere. Amounts are whole cents; passwords, API keys and webhook secrets are left out. Reports download as CSV from each report.</p>
        <Button loading={busy} onClick={() => void download()}>
          <Icons.Download size={14} /> Download all data
        </Button>
      </div>
    </Card>
  );
}

function DeleteCompanyCard() {
  const [open, setOpen] = useState(false);
  return (
    <Card title="Delete company" className="lg:col-span-2">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="max-w-xl text-[13px] text-ink-2">Deletes the company and all its books, for good. Download your data first: this can't be undone.</p>
        <Button variant="danger" onClick={() => setOpen(true)}>
          Delete company
        </Button>
      </div>
      {open && <DeleteCompanyModal onClose={() => setOpen(false)} />}
    </Card>
  );
}

function DeleteCompanyModal({ onClose }: { onClose: () => void }) {
  const { org, me } = useSession();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [f, setF] = useState({ name: '', password: '' });
  const onlyCompany = me.orgs.length <= 1;
  // Not useAction: refetching everything would only meet the deleted session.
  const run = useMutation<{ orgId: string | null; accountDeleted: boolean }, ApiError>({ mutationFn: () => api('/api/org', { method: 'DELETE', body: f }) });
  function deleted(r: { orgId: string | null; accountDeleted: boolean }) {
    qc.clear();
    if (r.orgId) {
      // Moved to the person's next company: a fresh load, like switching companies.
      navigate('/', { replace: true });
      window.location.reload();
      return;
    }
    sessionHint.set(false);
    navigate('/login', { replace: true, state: { notice: `${org.name} is deleted${r.accountDeleted ? ', and so is your account' : ''}.` } });
  }
  return (
    <Modal
      open
      onClose={onClose}
      title={`Delete ${org.name}?`}
      footer={
        <Button type="submit" form="delete-company" variant="danger" loading={run.isPending} disabled={f.name.trim() !== org.name.trim() || !f.password}>
          Delete for good
        </Button>
      }
    >
      <form id="delete-company" className="space-y-4" onSubmit={(e) => (e.preventDefault(), run.mutate(undefined, { onSuccess: deleted }))}>
        <ul className="list-disc space-y-1 pl-5 text-[13px] text-ink-2">
          <li>Every entry, invoice, bill, contract, bank line, report setting and the audit log is deleted. This can't be undone.</li>
          <li>Everyone in the company loses access. People with no other company in RareMargin lose their account too.</li>
          <li>{onlyCompany ? 'This is your only company, so your account is deleted and you are signed out.' : 'You move to your next company.'}</li>
          <li>A paid subscription must be cancelled first, in Billing.</li>
        </ul>
        <Field label={`Type the company name, ${org.name}`}>
          <Input autoFocus required autoComplete="off" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
        </Field>
        <Field label="Your password">
          <Input type="password" autoComplete="current-password" required value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} />
        </Field>
        {run.error && <ErrorNote error={run.error} />}
      </form>
    </Modal>
  );
}

// --- Exchange rates ---------------------------------------------------------------------------------------------

const CURRENCIES = ['USD', 'EUR', 'GBP', 'INR', 'CAD', 'AUD', 'SGD', 'JPY'];

interface Rate {
  date: string;
  fromCurrency: string;
  toCurrency: string;
  rate: string;
}

function RatesTab() {
  const { currency, can } = useSession();
  const q = useApi<Rate[]>('/api/exchange-rates');
  const [f, setF] = useState({ from: currency === 'EUR' ? 'USD' : 'EUR', to: currency, date: todayIso(), rate: '' });
  const rate = Number(f.rate);
  const valid = f.from !== f.to && !!f.date && Number.isFinite(rate) && rate > 0;
  const save = useAction(() => api('/api/exchange-rates', { body: { date: f.date, from: f.from, to: f.to, rate: f.rate.trim() } }), { success: `Rate saved: 1 ${f.from} = ${f.rate.trim()} ${f.to}` });
  return (
    <div className="grid max-w-5xl gap-5 lg:grid-cols-[minmax(0,1fr)_320px]">
      <Card pad={false} title="Exchange rates" subtitle={`Invoices, bills and entries in another currency convert to ${currency} at the latest rate on or before their date.`}>
        <Query q={q}>
          {(rows) =>
            rows.length === 0 ? (
              <Empty title="No exchange rates yet">Only needed when you bill, pay or book in a currency other than {currency}.</Empty>
            ) : (
              <Table>
                <thead>
                  <tr>
                    <Th>Date</Th>
                    <Th>Pair</Th>
                    <Th right>Rate</Th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={`${r.fromCurrency}${r.toCurrency}${r.date}`}>
                      <Td>{date(r.date)}</Td>
                      <Td>
                        {r.fromCurrency} → {r.toCurrency}
                      </Td>
                      <Td right className="num">
                        1 {r.fromCurrency} = {Number(r.rate)} {r.toCurrency}
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )
          }
        </Query>
      </Card>
      {can('ACCOUNTANT') && (
        <Card title="Add a rate" subtitle="A later date adds a new rate; the same date replaces it.">
          <div className="grid grid-cols-2 gap-3">
            <Field label="From">
              <Select value={f.from} onChange={(e) => setF({ ...f, from: e.target.value })}>
                {CURRENCIES.map((c) => (
                  <option key={c}>{c}</option>
                ))}
              </Select>
            </Field>
            <Field label="To">
              <Select value={f.to} onChange={(e) => setF({ ...f, to: e.target.value })}>
                {CURRENCIES.map((c) => (
                  <option key={c}>{c}</option>
                ))}
              </Select>
            </Field>
            <Field label="Date">
              <Input type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} />
            </Field>
            <Field label={`${f.to} per 1 ${f.from}`} error={f.rate && !(rate > 0) ? 'Enter a positive number' : f.from === f.to ? 'Pick two currencies' : undefined}>
              <Input inputMode="decimal" value={f.rate} onChange={(e) => setF({ ...f, rate: e.target.value })} placeholder="1.25" />
            </Field>
          </div>
          {save.error && <ErrorNote error={save.error} className="mt-3" />}
          <Button variant="primary" className="mt-4 w-full" disabled={!valid} loading={save.isPending} onClick={() => save.mutate(undefined, { onSuccess: () => setF({ ...f, rate: '' }) })}>
            Save rate
          </Button>
        </Card>
      )}
    </div>
  );
}

// --- Members ------------------------------------------------------------------------------------------------------

const ROLE_INFO: Record<Role, string> = {
  OWNER: 'Everything, including billing and deleting the company.',
  ADMIN: 'Settings, API keys, webhooks, approvals, closing and reopening periods.',
  ACCOUNTANT: 'Day-to-day bookkeeping: entries, invoices, bills, reconciliation. Can request but not grant approvals above policy.',
  VIEWER: 'Read-only access to the ledger and reports.',
};
const ROLE_TONE: Record<Role, 'brand' | 'info' | 'neutral' | 'ai'> = { OWNER: 'brand', ADMIN: 'info', ACCOUNTANT: 'neutral', VIEWER: 'neutral' };

// --- Your account -------------------------------------------------------------------------------------------------

function AccountTab() {
  return (
    <div className="grid max-w-3xl gap-5">
      <ProfileCard />
      <PasswordCard />
      <TwoFactorCard />
    </div>
  );
}

function ProfileCard() {
  const { me } = useSession();
  const [name, setName] = useState(me.user.name);
  const save = useAction(() => api('/api/account', { method: 'PATCH', body: { name } }), { success: 'Name saved' });
  const resend = useAction(() => api<{ alreadyVerified: boolean; email?: string }>('/api/auth/verify-email/resend', { method: 'POST' }), {
    success: (r) => (r.alreadyVerified ? 'Your email is already confirmed' : `Sent a new link to ${r.email}`),
  });
  return (
    <Card title="Profile" subtitle="How teammates see you in approvals, comments and the audit log.">
      <form className="grid gap-4 sm:grid-cols-2" onSubmit={(e) => (e.preventDefault(), save.mutate())}>
        <Field label="Name">
          <Input required maxLength={120} value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field label="Email" hint={me.user.emailVerified ? 'Confirmed' : 'Not confirmed yet: the assistant and invites wait for it.'}>
          <div className="flex items-center gap-2">
            <Input value={me.user.email} readOnly className="flex-1" />
            {!me.user.emailVerified && (
              <Button size="sm" loading={resend.isPending} onClick={() => resend.mutate()}>
                Send link
              </Button>
            )}
          </div>
        </Field>
        <div className="sm:col-span-2">
          <Button type="submit" variant="primary" loading={save.isPending} disabled={!name.trim() || name.trim() === me.user.name}>
            Save
          </Button>
        </div>
      </form>
    </Card>
  );
}

function PasswordCard() {
  const empty = { currentPassword: '', newPassword: '', confirm: '' };
  const [f, setF] = useState(empty);
  const mismatch = !!f.confirm && f.confirm !== f.newPassword;
  const change = useAction(() => api('/api/account/password', { body: { currentPassword: f.currentPassword, newPassword: f.newPassword } }), {
    success: 'Password changed. Other browsers and devices were signed out.',
  });
  return (
    <Card title="Password" subtitle="Changing it signs you out everywhere else.">
      <form className="grid gap-4 sm:grid-cols-3" onSubmit={(e) => (e.preventDefault(), change.mutate(undefined, { onSuccess: () => setF(empty) }))}>
        <Field label="Current password">
          <Input type="password" autoComplete="current-password" required value={f.currentPassword} onChange={(e) => setF({ ...f, currentPassword: e.target.value })} />
        </Field>
        <Field label="New password" hint="At least 10 characters">
          <Input type="password" autoComplete="new-password" required minLength={10} value={f.newPassword} onChange={(e) => setF({ ...f, newPassword: e.target.value })} />
        </Field>
        <Field label="Confirm new password" error={mismatch ? 'The two passwords do not match' : undefined}>
          <Input type="password" autoComplete="new-password" required minLength={10} invalid={mismatch} value={f.confirm} onChange={(e) => setF({ ...f, confirm: e.target.value })} />
        </Field>
        <div className="sm:col-span-3">
          <Button type="submit" variant="primary" loading={change.isPending} disabled={mismatch || !f.confirm}>
            Change password
          </Button>
        </div>
      </form>
    </Card>
  );
}

interface AccountSettings {
  twoFactor: { enabled: boolean; recoveryCodesLeft: number };
}

function TwoFactorCard() {
  const q = useApi<AccountSettings>('/api/account');
  const [dialog, setDialog] = useState<'setup' | 'codes' | 'off' | null>(null);
  const tf = q.data?.twoFactor;
  return (
    <Card
      title="Two-factor sign-in"
      subtitle="A code from an authenticator app (1Password, Google Authenticator, Authy) each time you sign in, on top of your password."
      actions={tf && <Badge tone={tf.enabled ? 'brand' : 'neutral'}>{tf.enabled ? 'On' : 'Off'}</Badge>}
    >
      <Query q={q}>
        {(a) =>
          a.twoFactor.enabled ? (
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className={cx('text-[13px]', a.twoFactor.recoveryCodesLeft <= 2 ? 'text-warn' : 'text-muted')}>
                {a.twoFactor.recoveryCodesLeft} of 10 recovery codes left{a.twoFactor.recoveryCodesLeft <= 2 && ': make new ones before you run out'}.
              </p>
              <div className="flex gap-2">
                <Button size="sm" onClick={() => setDialog('codes')}>
                  New recovery codes
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setDialog('off')}>
                  Turn off
                </Button>
              </div>
            </div>
          ) : (
            <Button variant="primary" onClick={() => setDialog('setup')}>
              Turn on
            </Button>
          )
        }
      </Query>
      {dialog === 'setup' && <TwoFactorSetup onClose={() => setDialog(null)} />}
      {(dialog === 'codes' || dialog === 'off') && <TwoFactorConfirm mode={dialog} onClose={() => setDialog(null)} />}
    </Card>
  );
}

/** Password, then scan and confirm a code, then the recovery codes, shown once. */
function TwoFactorSetup({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [setup, setSetup] = useState<{ secret: string; uri: string; qr: string } | null>(null);
  const [codes, setCodes] = useState<string[] | null>(null);
  const start = useAction(() => api<{ secret: string; uri: string; qr: string }>('/api/account/2fa/setup', { body: { password } }));
  const enable = useAction(() => api<{ recoveryCodes: string[] }>('/api/account/2fa/enable', { body: { code } }));
  const done = () => (void qc.invalidateQueries({ queryKey: ['/api/account'] }), onClose());

  if (codes) {
    return (
      <Modal open onClose={done} title="Save your recovery codes" footer={<Button variant="primary" onClick={done}>I have saved them</Button>}>
        <RecoveryCodes codes={codes} />
      </Modal>
    );
  }
  if (setup) {
    return (
      <Modal
        open
        onClose={onClose}
        title="Scan with your authenticator app"
        footer={
          <Button type="submit" form="tf-enable" variant="primary" loading={enable.isPending}>
            Turn on
          </Button>
        }
      >
        <form id="tf-enable" className="space-y-4" onSubmit={(e) => (e.preventDefault(), enable.mutate(undefined, { onSuccess: (r) => setCodes(r.recoveryCodes) }))}>
          <div className="flex flex-col items-center gap-4 sm:flex-row sm:items-start">
            <img src={setup.qr} alt="QR code for your authenticator app" className="size-40 shrink-0 rounded-lg bg-white p-1.5" />
            {/* On a phone the app is on the same screen, so the ways that need no scanning come first there. */}
            <div className="w-full min-w-0 space-y-3.5 text-[13px] text-ink-2 max-sm:order-first">
              <p className="max-sm:hidden">Scan the code with the authenticator app on your phone.</p>
              <div>
                <p className="text-[12.5px] text-muted">Is the app on this device?</p>
                {/* The link holds the secret: no data-track, so usage counts never see it. */}
                <a
                  href={setup.uri}
                  className="mt-1.5 inline-flex h-8 items-center gap-1.5 rounded-[10px] border border-line-strong/90 bg-white/80 px-3 text-[13px] font-medium text-ink shadow-[0_1px_2px_rgb(27_26_24/0.05)] transition-colors hover:border-faint/70 hover:bg-white"
                >
                  <Icons.Key size={14} />
                  Add it to my authenticator app
                </a>
              </div>
              <div>
                <p className="text-[12.5px] text-muted">Or type this key into the app:</p>
                <div className="mt-1.5 flex items-center gap-2">
                  <code className="min-w-0 flex-1 rounded-md bg-sunken px-2 py-1.5 font-mono text-[12.5px] break-all select-all">{setup.secret.match(/.{1,4}/g)!.join(' ')}</code>
                  <CopyButton text={setup.secret} label="Copy key" />
                </div>
              </div>
            </div>
          </div>
          <Field label="The 6-digit code the app shows now">
            <Input autoFocus required inputMode="numeric" autoComplete="one-time-code" maxLength={7} className="font-mono tracking-[0.2em]" value={code} onChange={(e) => setCode(e.target.value)} />
          </Field>
          <p className="text-[12px] text-muted">Turning it on signs you out on your other browsers and devices.</p>
        </form>
      </Modal>
    );
  }
  return (
    <Modal
      open
      onClose={onClose}
      title="Turn on two-factor sign-in"
      footer={
        <Button type="submit" form="tf-start" variant="primary" loading={start.isPending}>
          Continue
        </Button>
      }
    >
      <form id="tf-start" className="space-y-3" onSubmit={(e) => (e.preventDefault(), start.mutate(undefined, { onSuccess: setSetup }))}>
        <Field label="Your password">
          <Input type="password" autoComplete="current-password" autoFocus required value={password} onChange={(e) => setPassword(e.target.value)} />
        </Field>
      </form>
    </Modal>
  );
}

/** New recovery codes, or turning it off: both ask for the password and a current code. */
function TwoFactorConfirm({ mode, onClose }: { mode: 'codes' | 'off'; onClose: () => void }) {
  const qc = useQueryClient();
  const [f, setF] = useState({ password: '', code: '' });
  const [codes, setCodes] = useState<string[] | null>(null);
  const run = useAction(() => api<{ recoveryCodes?: string[] }>(mode === 'off' ? '/api/account/2fa/disable' : '/api/account/2fa/recovery-codes', { body: f }), {
    success: mode === 'off' ? 'Two-factor sign-in is off' : undefined,
  });
  const done = () => (void qc.invalidateQueries({ queryKey: ['/api/account'] }), onClose());
  if (codes) {
    return (
      <Modal open onClose={done} title="Your new recovery codes" footer={<Button variant="primary" onClick={done}>I have saved them</Button>}>
        <RecoveryCodes codes={codes} />
      </Modal>
    );
  }
  return (
    <Modal
      open
      onClose={onClose}
      title={mode === 'off' ? 'Turn off two-factor sign-in' : 'Make new recovery codes'}
      footer={
        <Button type="submit" form="tf-confirm" variant={mode === 'off' ? 'danger' : 'primary'} loading={run.isPending}>
          {mode === 'off' ? 'Turn off' : 'Make new codes'}
        </Button>
      }
    >
      <form id="tf-confirm" className="space-y-3" onSubmit={(e) => (e.preventDefault(), run.mutate(undefined, { onSuccess: (r) => (r.recoveryCodes ? setCodes(r.recoveryCodes) : done()) }))}>
        {mode === 'codes' && <p className="text-[13px] text-ink-2">Your current recovery codes stop working.</p>}
        <Field label="Your password">
          <Input type="password" autoComplete="current-password" autoFocus required value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} />
        </Field>
        <Field label="A code from your authenticator app, or a recovery code">
          <Input required autoComplete="one-time-code" className="font-mono tracking-[0.15em]" value={f.code} onChange={(e) => setF({ ...f, code: e.target.value })} />
        </Field>
      </form>
    </Modal>
  );
}

/** Shown once: each code signs in once if the phone is lost. */
function RecoveryCodes({ codes }: { codes: string[] }) {
  const text = `RareMargin recovery codes. Each one works once.\n\n${codes.join('\n')}\n`;
  function download() {
    const url = URL.createObjectURL(new Blob([text], { type: 'text/plain;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = 'raremargin-recovery-codes.txt';
    a.click();
    URL.revokeObjectURL(url);
  }
  return (
    <div className="space-y-3">
      <p className="text-[13px] text-ink-2">If you lose your phone, each of these signs you in once. Keep them somewhere safe, like a password manager. You won't see them again.</p>
      <ol className="grid grid-cols-2 gap-x-6 gap-y-1 rounded-lg bg-sunken px-4 py-3 font-mono text-[13px]">
        {codes.map((c) => (
          <li key={c}>{c}</li>
        ))}
      </ol>
      <div className="flex gap-2">
        <Button size="sm" onClick={() => void navigator.clipboard.writeText(text).then(() => toast.success('Copied'))}>
          Copy
        </Button>
        <Button size="sm" onClick={download}>
          Download
        </Button>
      </div>
    </div>
  );
}

function MembersTab() {
  const { me, can } = useSession();
  const q = useApi<Members>('/api/members');
  const invites = useApi<Invite[]>(can('ADMIN') ? '/api/invites' : null);
  const [inviting, setInviting] = useState(false);
  const remove = useAction((m: Members[number]) => api(`/api/members/${m.userId}`, { method: 'DELETE' }), { success: 'Member removed' });
  const setRole = useAction((v: { m: Members[number]; role: Role }) => api(`/api/members/${v.m.userId}`, { method: 'PATCH', body: { role: v.role } }), {
    success: (r) => `${titleCase((r as { role: Role }).role)} now`,
  });
  // Admins manage everyone but owners; only an owner makes or changes an owner (the server keeps at least one).
  const canChangeRole = (m: Members[number]) => can('ADMIN') && (m.role !== 'OWNER' || me.role === 'OWNER');
  function pickRole(m: Members[number], role: Role) {
    if (role === 'OWNER' && !confirm(`Make ${m.name} an owner? Owners can do everything, including managing billing and other owners.`)) return;
    if (m.userId === me.user.id && !confirm(`Change your own role to ${titleCase(role)}? You may lose access to some settings.`)) return;
    setRole.mutate({ m, role });
  }
  const revoke = useAction((id: string) => api(`/api/invites/${id}`, { method: 'DELETE' }), { success: 'Invite revoked' });
  // Owners can remove anyone but the last owner (the server checks that); admins can remove everyone but owners.
  const canRemove = (m: Members[number]) => can('ADMIN') && m.userId !== me.user.id && (m.role !== 'OWNER' || me.role === 'OWNER');
  return (
    <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_380px]">
      <div className="space-y-5">
        <Card
          pad={false}
          title="People"
          actions={
            can('ADMIN') && (
              <Button
                variant="primary"
                size="sm"
                icon={<Icons.Plus size={14} />}
                onClick={() => setInviting(true)}
                disabled={!me.user.emailVerified}
                title={me.user.emailVerified ? undefined : 'Confirm your email first: open the link we sent you'}
              >
                Invite
              </Button>
            )
          }
        >
          <Query q={q}>
            {(rows) => (
              <Table>
                <thead>
                  <tr>
                    <Th>Name</Th>
                    <Th>Role</Th>
                    <Th>Member since</Th>
                    <Th />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((m) => (
                    <tr key={m.userId}>
                      <Td>
                        <div className="flex items-center gap-2.5">
                          <Avatar name={m.name} />
                          <div>
                            <div className="font-medium">
                              {m.name} {m.userId === me.user.id && <span className="text-[12px] font-normal text-muted">(you)</span>}
                            </div>
                            <div className="text-[12px] text-muted">{m.email}</div>
                          </div>
                        </div>
                      </Td>
                      <Td>
                        {canChangeRole(m) ? (
                          <Select
                            aria-label={`Role for ${m.name}`}
                            className="h-8 w-36"
                            value={m.role}
                            disabled={setRole.isPending && setRole.variables?.m.userId === m.userId}
                            onChange={(e) => pickRole(m, e.target.value as Role)}
                          >
                            {(['OWNER', 'ADMIN', 'ACCOUNTANT', 'VIEWER'] as const)
                              .filter((r) => r !== 'OWNER' || me.role === 'OWNER')
                              .map((r) => (
                                <option key={r} value={r}>
                                  {titleCase(r)}
                                </option>
                              ))}
                          </Select>
                        ) : (
                          <Badge tone={ROLE_TONE[m.role as Role] ?? 'neutral'}>{titleCase(m.role)}</Badge>
                        )}
                      </Td>
                      <Td muted>{date(m.since)}</Td>
                      <Td right>
                        {canRemove(m) && (
                          <Button
                            size="sm"
                            variant="ghost"
                            loading={remove.isPending && remove.variables?.userId === m.userId}
                            onClick={() => confirm(`Remove ${m.name} from this company? They are signed out of it at once.`) && remove.mutate(m)}
                          >
                            Remove
                          </Button>
                        )}
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Query>
        </Card>
        {can('ADMIN') && !!invites.data?.length && (
          <Card pad={false} title="Pending invites" subtitle="Each link works once and expires 7 days after it was sent.">
            <Table>
              <thead>
                <tr>
                  <Th>Email</Th>
                  <Th>Role</Th>
                  <Th>Expires</Th>
                  <Th />
                </tr>
              </thead>
              <tbody>
                {invites.data.map((i) => (
                  <tr key={i.id}>
                    <Td className="font-medium">{i.email}</Td>
                    <Td>
                      <Badge tone={ROLE_TONE[i.role as Role] ?? 'neutral'}>{titleCase(i.role)}</Badge>
                    </Td>
                    <Td muted>{relative(i.expiresAt)}</Td>
                    <Td right>
                      <Button size="sm" variant="ghost" loading={revoke.isPending && revoke.variables === i.id} onClick={() => confirm(`Revoke the invite for ${i.email}? The link stops working.`) && revoke.mutate(i.id)}>
                        Revoke
                      </Button>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card>
        )}
      </div>
      <Card title="Roles">
        <dl className="space-y-3">
          {(Object.keys(ROLE_INFO) as Role[]).map((r) => (
            <div key={r}>
              <dt>
                <Badge tone={ROLE_TONE[r]}>{titleCase(r)}</Badge>
              </dt>
              <dd className="mt-1 text-[12.5px] text-muted">{ROLE_INFO[r]}</dd>
            </div>
          ))}
        </dl>
      </Card>
      {inviting && <InviteModal onClose={() => setInviting(false)} />}
    </div>
  );
}

function InviteModal({ onClose }: { onClose: () => void }) {
  const [f, setF] = useState({ email: '', role: 'ACCOUNTANT' });
  const [created, setCreated] = useState<CreatedInvite | null>(null);
  const create = useAction(() => api<CreatedInvite>('/api/invites', { body: f }), { silent: true });
  const fieldErr = (p: string) => create.error?.fieldErrors.find((e) => e.path === p)?.message;
  return (
    <Modal
      open
      onClose={onClose}
      title={created ? `Invite ready for ${created.email}` : 'Invite a teammate'}
      footer={
        created ? (
          <>
            <Button onClick={() => (setCreated(null), setF({ email: '', role: f.role }), create.reset())}>Invite someone else</Button>
            <Button variant="primary" onClick={onClose}>
              Done
            </Button>
          </>
        ) : (
          <>
            <Button onClick={onClose}>Cancel</Button>
            <Button type="submit" form="invite-form" variant="primary" disabled={!f.email.trim()} loading={create.isPending}>
              Send invite
            </Button>
          </>
        )
      }
    >
      {created ? (
        <div className="space-y-3">
          <p className="text-[12.5px] text-muted">
            {created.emailed
              ? `We emailed ${created.email} a link to join. You can also send them this link yourself.`
              : `Email isn't set up yet, so the invite was not emailed. Send ${created.email} this link yourself.`}{' '}
            It works once and expires in 7 days. This is the only time the link is shown.
          </p>
          <div className="flex items-center gap-2 rounded-md border border-line bg-sunken p-2">
            <code className="min-w-0 flex-1 truncate text-[12px]">{created.link}</code>
            <CopyButton text={created.link} />
          </div>
        </div>
      ) : (
        <form id="invite-form" className="space-y-3" onSubmit={(e) => (e.preventDefault(), create.mutate(undefined, { onSuccess: (r) => setCreated(r) }))}>
          <Field label="Email" error={fieldErr('email')}>
            <Input type="email" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} placeholder="name@company.com" autoFocus />
          </Field>
          <Field label="Role" hint={ROLE_INFO[f.role as Role]}>
            <Select value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })}>
              <option value="VIEWER">Viewer</option>
              <option value="ACCOUNTANT">Accountant</option>
              <option value="ADMIN">Admin</option>
            </Select>
          </Field>
          {create.error && !create.error.fieldErrors.length && <ErrorNote error={create.error} />}
        </form>
      )}
    </Modal>
  );
}

// --- Billing ------------------------------------------------------------------------------------------------------

const STATE_BADGE: Record<Access['state'], { label: string; tone: 'good' | 'warn' | 'bad' | 'info' | 'neutral' }> = {
  off: { label: 'Not configured', tone: 'neutral' },
  trialing: { label: 'Free trial', tone: 'info' },
  active: { label: 'Active', tone: 'good' },
  past_due: { label: 'Payment problem', tone: 'warn' },
  locked: { label: 'Read-only', tone: 'bad' },
};

/** After Dodo's checkout: re-read the subscription now rather than waiting for the webhook, then say how it went. */
function useCheckoutReturn() {
  const [params, setParams] = useSearchParams();
  const qc = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const handled = useRef(false);
  useEffect(() => {
    if (params.get('checkout') !== 'done' || handled.current) return;
    handled.current = true;
    const subscriptionId = params.get('subscription_id') ?? undefined;
    const status = params.get('status');
    setParams({ tab: 'billing' }, { replace: true });
    setConfirming(true);
    void api<Access>('/api/billing/refresh', { body: { subscriptionId } })
      .then((a) => {
        if (a.state === 'active') toast.success('You are subscribed. Thank you!');
        else if (status === 'failed') toast.error("The payment didn't go through. Try again, or use a different card.");
        else {
          toast.info('Your subscription turns on as soon as Dodo Payments confirms the payment, usually within a minute.');
          setTimeout(() => void qc.invalidateQueries({ queryKey: ['/api/billing'] }), 8000);
        }
      })
      .catch((err: Error) => toast.error(err.message))
      .finally(() => {
        setConfirming(false);
        void qc.invalidateQueries();
      });
  }, [params, setParams, qc]);
  return confirming;
}

function BillingTab() {
  const q = useBilling();
  const confirming = useCheckoutReturn();
  return <Query q={q}>{(b) => <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_380px]">{b.enabled ? <PlanCard b={b} confirming={confirming} /> : <BillingOff />}<UsageCard b={b} /></div>}</Query>;
}

function BillingOff() {
  const { me } = useSession();
  return (
    <Card title="Billing">
      {me.role === 'OWNER' ? (
        <div className="space-y-2 text-[13px] text-ink-2">
          <p>Billing is not configured on this server, so nobody is charged and no company is ever locked.</p>
          <p className="text-muted">
            To charge customers, set <code className="font-mono text-[12px]">DODO_PAYMENTS_API_KEY</code> and <code className="font-mono text-[12px]">DODO_PRODUCT_ID</code> (and{' '}
            <code className="font-mono text-[12px]">DODO_PAYMENTS_WEBHOOK_KEY</code>), then restart. The README explains each one.
          </p>
        </div>
      ) : (
        <p className="text-[13px] text-muted">Billing is not set up for this company.</p>
      )}
    </Card>
  );
}

function PlanCard({ b, confirming }: { b: Billing; confirming: boolean }) {
  const subscribe = useSubscribe();
  const manage = useManageBilling();
  const { access: a, plan } = b;
  const badge = STATE_BADGE[a.state];
  const price = plan && planPrice(plan);

  let status: ReactNode;
  if (a.state === 'trialing') {
    status = (
      <>
        Your free trial has <b className="font-semibold">{days(a.trialDaysLeft ?? 0)}</b> left and ends on {date(a.trialEndsAt)}. Subscribe any time: the days you have left carry over, and nothing is charged until the trial
        ends.
      </>
    );
  } else if (a.state === 'active') {
    status = a.cancelAtPeriodEnd
      ? `Your subscription is cancelled. Everything keeps working until ${date(a.currentPeriodEnd)}.`
      : a.currentPeriodEnd
        ? `Your subscription renews on ${date(a.currentPeriodEnd)}.`
        : 'You are subscribed.';
  } else if (a.state === 'past_due') {
    status = "The last payment didn't go through. Dodo Payments will retry it; update your card in Manage billing so nothing is interrupted.";
  } else {
    status = a.reason;
  }

  return (
    <Card
      title="Plan"
      subtitle="One plan per company, with everything included."
      actions={b.mode === 'test_mode' && <Badge tone="warn">Test mode</Badge>}
    >
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="text-[13px] font-medium text-ink-2">{plan?.name ?? 'RareMargin'}</div>
          {price ? (
            <div className="mt-0.5">
              <span className="num text-[26px] font-semibold tracking-[-0.01em]">{price.amount}</span> <span className="text-[13px] text-muted">{price.per}</span>
            </div>
          ) : (
            <div className="mt-1 text-[12.5px] text-muted">The price could not be loaded from Dodo Payments. Check the product ID, or try again in a moment.</div>
          )}
        </div>
        <Badge tone={badge.tone} dot>
          {badge.label}
        </Badge>
      </div>
      <p className={cx('mt-4 text-[13px] leading-relaxed', a.state === 'locked' ? 'text-bad' : a.state === 'past_due' ? 'text-warn' : 'text-ink-2')}>{status}</p>
      {confirming && <p className="mt-2 text-[12.5px] text-muted">Checking your payment with Dodo Payments…</p>}
      {b.canManage ? (
        <div className="mt-5 flex flex-wrap gap-2">
          {(a.state === 'trialing' || a.state === 'locked') && (
            <Button variant="primary" loading={subscribe.pending} onClick={subscribe.start}>
              {a.state === 'trialing' ? 'Subscribe now' : 'Subscribe'}
            </Button>
          )}
          {b.hasCustomer && (
            <Button variant={a.state === 'past_due' ? 'primary' : 'secondary'} loading={manage.pending} onClick={manage.start}>
              Manage billing
            </Button>
          )}
        </div>
      ) : (
        <p className="mt-4 text-[12.5px] text-muted">Only owners and admins can change billing.</p>
      )}
      {b.canManage && b.hasCustomer && <p className="mt-3 text-[12px] text-muted">Manage billing opens Dodo Payments, where you can change your card, download invoices or cancel.</p>}
    </Card>
  );
}

function UsageCard({ b }: { b: Billing }) {
  const { used, limit, resetsAt } = b.ai;
  const ratio = limit > 0 ? used / limit : 1;
  return (
    <Card title="Assistant usage" subtitle="Messages to the AI assistant this calendar month.">
      <div className="num text-[22px] font-semibold tracking-[-0.01em]">
        {used.toLocaleString()} <span className="text-[13px] font-normal text-muted">of {limit.toLocaleString()}</span>
      </div>
      <Progress className="mt-2" value={ratio} tone={ratio >= 1 ? 'bad' : ratio >= 0.8 ? 'warn' : 'brand'} />
      <p className="mt-3 text-[12.5px] text-muted">
        {limit === 0
          ? 'The assistant is paused until the company subscribes.'
          : used >= limit
            ? `The limit is reached. It resets on ${date(resetsAt)}.`
            : `Resets on ${date(resetsAt)}.`}
        {b.access.state === 'trialing' && ' The trial includes a smaller allowance; subscribing raises it.'}
      </p>
    </Card>
  );
}

// --- API keys and MCP ---------------------------------------------------------------------------------------------

function CopyButton({ text, label = 'Copy' }: { text: string; label?: string }) {
  const [done, setDone] = useState(false);
  return (
    <Button
      size="sm"
      icon={done ? <Icons.Check size={13} /> : <Icons.Copy size={13} />}
      onClick={() => {
        void navigator.clipboard?.writeText(text).then(() => {
          setDone(true);
          setTimeout(() => setDone(false), 1500);
        });
      }}
    >
      {done ? 'Copied' : label}
    </Button>
  );
}

function Snippet({ children }: { children: string }) {
  return (
    <div className="group relative">
      <pre className="overflow-x-auto rounded-md bg-sidebar p-3 pr-20 font-mono text-[11.5px] leading-relaxed text-white/90">{children}</pre>
      <div className="absolute top-2 right-2">
        <CopyButton text={children} />
      </div>
    </div>
  );
}

function DevelopersTab() {
  const { can } = useSession();
  const q = useApi<ApiKey[]>('/api/api-keys');
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState<CreatedApiKey | null>(null);
  const [f, setF] = useState({ name: '', role: 'ACCOUNTANT' });
  const create = useAction(() => api<CreatedApiKey>('/api/api-keys', { body: f }), { success: 'API key created' });
  const revoke = useAction((id: string) => api(`/api/api-keys/${id}`, { method: 'DELETE' }), { success: 'API key revoked' });
  const origin = window.location.origin;
  const keyText = created?.key ?? 'ck_…';
  return (
    <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_440px]">
      <div className="space-y-5">
        <Card
          pad={false}
          title="API keys"
          subtitle="Keys authenticate the public REST API (/v1) and the MCP server. Each key acts with the role you give it."
          actions={
            can('ADMIN') && (
              <Button variant="primary" size="sm" icon={<Icons.Plus size={14} />} onClick={() => (setCreated(null), setF({ name: '', role: 'ACCOUNTANT' }), setCreating(true))}>
                New key
              </Button>
            )
          }
        >
          <Query q={q}>
            {(rows) =>
              rows.length === 0 ? (
                <Empty title="No API keys yet" icon={<Icons.Key size={20} />}>
                  Create one to connect scripts, a data warehouse, or an AI agent over MCP.
                </Empty>
              ) : (
                <Table>
                  <thead>
                    <tr>
                      <Th>Name</Th>
                      <Th>Key</Th>
                      <Th>Role</Th>
                      <Th>Last used</Th>
                      <Th>Status</Th>
                      <Th />
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((k) => (
                      <tr key={k.id}>
                        <Td className="font-medium">{k.name}</Td>
                        <Td>
                          <code className="text-[12px] text-ink-2">{k.prefix}…</code>
                        </Td>
                        <Td muted>{titleCase(k.role)}</Td>
                        <Td muted>{k.lastUsedAt ? relative(k.lastUsedAt) : 'Never'}</Td>
                        <Td>{k.revokedAt ? <Badge>Revoked</Badge> : <Badge tone="good" dot>Active</Badge>}</Td>
                        <Td right>
                          {!k.revokedAt && can('ADMIN') && (
                            <Button size="sm" variant="ghost" loading={revoke.isPending && revoke.variables === k.id} onClick={() => confirm(`Revoke “${k.name}”? Anything using it stops working immediately.`) && revoke.mutate(k.id)}>
                              Revoke
                            </Button>
                          )}
                        </Td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )
            }
          </Query>
        </Card>
        <ConnectedAppsCard />
        <Card title="REST API" subtitle="Snake_case resources with decimal-string amounts, signed cursors, and Idempotency-Key support on writes.">
          <Snippet>{`curl ${origin}/v1/invoices?limit=5 \\\n  -H "Authorization: Bearer ${keyText}"`}</Snippet>
        </Card>
      </div>
      <Card title="Connect an AI agent over MCP" subtitle="RareMargin exposes its ledger tools over the Model Context Protocol (streamable HTTP). Writes become proposals that a person approves.">
        <div className="space-y-3 text-[12.5px] text-ink-2">
          <div>
            <div className="mb-1 font-medium">Claude, ChatGPT and other apps that sign in</div>
            <p className="mb-1.5 text-muted">Add a custom connector with this address. The app opens RareMargin to sign in, and you choose the company and whether it reads only or also drafts.</p>
            <Snippet>{`${origin}/mcp`}</Snippet>
          </div>
          <div>
            <div className="mb-1 font-medium">Claude Code, signing in</div>
            <Snippet>{`claude mcp add --transport http raremargin ${origin}/mcp`}</Snippet>
          </div>
          <div>
            <div className="mb-1 font-medium">Claude Code, with an API key</div>
            <Snippet>{`claude mcp add --transport http raremargin ${origin}/mcp \\\n  --header "Authorization: Bearer ${keyText}"`}</Snippet>
          </div>
          <div>
            <div className="mb-1 font-medium">Any MCP client (JSON config)</div>
            <Snippet>{JSON.stringify({ mcpServers: { raremargin: { type: 'http', url: `${origin}/mcp`, headers: { Authorization: `Bearer ${keyText}` } } } }, null, 2)}</Snippet>
          </div>
          <p className="text-muted">Use a Viewer key for read-only agents. Accountant keys can draft entries and accruals; they still wait in the approvals inbox.</p>
        </div>
      </Card>

      <Modal
        open={creating}
        onClose={() => setCreating(false)}
        title={created ? 'Copy your new key' : 'New API key'}
        footer={
          created ? (
            <Button variant="primary" onClick={() => setCreating(false)}>
              Done
            </Button>
          ) : (
            <>
              <Button onClick={() => setCreating(false)}>Cancel</Button>
              <Button variant="primary" disabled={!f.name.trim()} loading={create.isPending} onClick={() => create.mutate(undefined, { onSuccess: (r) => setCreated(r) })}>
                Create key
              </Button>
            </>
          )
        }
      >
        {created ? (
          <div className="space-y-3">
            <p className="text-[12.5px] text-muted">This is the only time the full key is shown. Store it in a secret manager.</p>
            <div className="flex items-center gap-2 rounded-md border border-line bg-sunken p-2">
              <code className="min-w-0 flex-1 truncate text-[12px]">{created.key}</code>
              <CopyButton text={created.key} />
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <Field label="Name" hint="Where it is used, e.g. “Snowflake sync” or “Claude agent”">
              <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} autoFocus />
            </Field>
            <Field label="Role">
              <Select value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })}>
                <option value="VIEWER">Viewer: read only</option>
                <option value="ACCOUNTANT">Accountant: read and draft</option>
                <option value="ADMIN">Admin: full access</option>
              </Select>
            </Field>
            {create.error && <ErrorNote error={create.error} />}
          </div>
        )}
      </Modal>
    </div>
  );
}

/** AI apps signed in with OAuth: everyone's for admins and owners, otherwise the person's own. */
function ConnectedAppsCard() {
  const { can } = useSession();
  const q = useApi<ConnectedApp[]>('/api/oauth/apps');
  const revoke = useAction((id: string) => api(`/api/oauth/apps/${id}`, { method: 'DELETE' }), { success: 'App disconnected' });
  const everyone = can('ADMIN');
  return (
    <Card
      pad={false}
      title="Connected apps"
      subtitle={
        everyone
          ? 'AI apps that people in this company signed in to with RareMargin, such as Claude and ChatGPT. Each acts with the access it was given, never more than its person’s own role.'
          : 'AI apps you signed in to with RareMargin, such as Claude and ChatGPT. Admins and owners see everyone’s.'
      }
    >
      <Query q={q}>
        {(rows) =>
          rows.length === 0 ? (
            <Empty title="No apps connected" icon={<Icons.Sparkle size={20} />}>
              In Claude or ChatGPT, add a custom connector with {window.location.origin}/mcp and sign in.
            </Empty>
          ) : (
            <Table>
              <thead>
                <tr>
                  <Th>App</Th>
                  {everyone && <Th>Connected by</Th>}
                  <Th>Access</Th>
                  <Th>Connected</Th>
                  <Th>Last used</Th>
                  <Th />
                </tr>
              </thead>
              <tbody>
                {rows.map((a) => (
                  <tr key={a.id}>
                    <Td>
                      <div className="font-medium">{a.app}</div>
                      <div className="text-[11.5px] text-muted">{a.host === 'localhost' ? 'On a computer (localhost)' : a.host}</div>
                    </Td>
                    {everyone && <Td muted>{a.userName ?? a.userEmail ?? 'A former member'}</Td>}
                    <Td muted>{a.role === 'ACCOUNTANT' ? 'Accountant: read and draft' : 'Viewer: read only'}</Td>
                    <Td muted>{date(a.createdAt)}</Td>
                    <Td muted>{a.lastUsedAt ? relative(a.lastUsedAt) : 'Never'}</Td>
                    <Td right>
                      <Button size="sm" variant="ghost" loading={revoke.isPending && revoke.variables === a.id} onClick={() => confirm(`Disconnect ${a.app}? It stops working at once, and has to sign in again to reconnect.`) && revoke.mutate(a.id)}>
                        Disconnect
                      </Button>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )
        }
      </Query>
    </Card>
  );
}

// --- Webhooks -----------------------------------------------------------------------------------------------------

function WebhooksTab() {
  const { can } = useSession();
  const q = useApi<{ webhooks: Webhook[]; eventTypes: string[] }>('/api/webhooks');
  const [creating, setCreating] = useState(false);
  const [open, setOpen] = useState<string | null>(null);
  const toggle = useAction(({ id, active }: { id: string; active: boolean }) => api(`/api/webhooks/${id}`, { method: 'PATCH', body: { active } }), { success: 'Webhook updated' });
  const remove = useAction((id: string) => api(`/api/webhooks/${id}`, { method: 'DELETE' }), { success: 'Webhook deleted' });
  const test = useAction((id: string) => api(`/api/webhooks/${id}/test`, { method: 'POST' }), { success: 'Test event queued' });
  return (
    <>
      <Card
        pad={false}
        title="Endpoints"
        subtitle={
          <>
            Events are signed with HMAC-SHA256: <code className="text-[11.5px]">X-RareMargin-Signature</code> carries <code className="text-[11.5px]">v1=</code> (base64) over <code className="text-[11.5px]">{'{id}.{timestamp}.{body}'}</code>, from the <code className="text-[11.5px]">X-RareMargin-Id</code> and <code className="text-[11.5px]">X-RareMargin-Timestamp</code> headers. Failed deliveries retry with backoff.
          </>
        }
        actions={
          can('ADMIN') && (
            <Button variant="primary" size="sm" icon={<Icons.Plus size={14} />} onClick={() => setCreating(true)}>
              Add endpoint
            </Button>
          )
        }
      >
        <Query q={q}>
          {({ webhooks }) =>
            webhooks.length === 0 ? (
              <Empty title="No webhook endpoints" icon={<Icons.Send size={20} />}>
                Push ledger events to Slack, a warehouse, or your own services as they happen.
              </Empty>
            ) : (
              <Table>
                <thead>
                  <tr>
                    <Th>Endpoint</Th>
                    <Th>Events</Th>
                    <Th>Secret</Th>
                    <Th>Active</Th>
                    <Th />
                  </tr>
                </thead>
                <tbody>
                  {webhooks.map((w) => (
                    <Fragment key={w.id}>
                      <tr className="hover:bg-paper">
                        <Td>
                          <button className="flex items-center gap-1.5 text-left" onClick={() => setOpen(open === w.id ? null : w.id)}>
                            <span className={cx('text-faint transition-transform', open === w.id && 'rotate-90')}>
                              <Icons.Chevron size={12} />
                            </span>
                            <span>
                              <span className="block max-w-md truncate font-medium">{w.url}</span>
                              {w.description && <span className="block text-[12px] text-muted">{w.description}</span>}
                            </span>
                          </button>
                        </Td>
                        <Td muted className="text-[12px]">
                          {w.events.includes('*') ? 'All events' : w.events.length <= 2 ? w.events.join(', ') : `${w.events.length} events`}
                        </Td>
                        <Td>
                          <code className="text-[12px] text-muted">{w.secretHint}</code>
                        </Td>
                        <Td>
                          <button
                            role="switch"
                            aria-checked={w.active}
                            aria-label="Active"
                            disabled={!can('ADMIN')}
                            onClick={() => toggle.mutate({ id: w.id, active: !w.active })}
                            className={cx('relative h-5 w-9 rounded-full transition-colors disabled:opacity-50', w.active ? 'bg-brand' : 'bg-line-strong')}
                          >
                            <span className={cx('absolute top-0.5 left-0.5 size-4 rounded-full bg-white shadow transition-transform', w.active && 'translate-x-4')} />
                          </button>
                        </Td>
                        <Td right>
                          {can('ADMIN') && (
                            <div className="flex justify-end gap-1">
                              <Button size="sm" loading={test.isPending && test.variables === w.id} onClick={() => test.mutate(w.id)}>
                                Send test
                              </Button>
                              <Button size="sm" variant="ghost" aria-label="Delete" onClick={() => confirm('Delete this endpoint?') && remove.mutate(w.id)}>
                                <Icons.Trash size={14} />
                              </Button>
                            </div>
                          )}
                        </Td>
                      </tr>
                      {open === w.id && (
                        <tr>
                          <td colSpan={5} className="bg-paper/60 px-4 py-3">
                            <Deliveries id={w.id} />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  ))}
                </tbody>
              </Table>
            )
          }
        </Query>
      </Card>
      {q.data && <NewWebhookModal open={creating} onClose={() => setCreating(false)} eventTypes={q.data.eventTypes} />}
    </>
  );
}

function Deliveries({ id }: { id: string }) {
  const q = useApi<WebhookDelivery[]>(`/api/webhooks/${id}/deliveries`, { refetchInterval: 5000 });
  return (
    <Query q={q}>
      {(rows) =>
        rows.length === 0 ? (
          <p className="text-[12.5px] text-muted">No deliveries yet. Send a test event to check the endpoint.</p>
        ) : (
          <Table dense>
            <thead>
              <tr>
                <Th>Event</Th>
                <Th>Status</Th>
                <Th right>Attempts</Th>
                <Th>Response</Th>
                <Th>Created</Th>
              </tr>
            </thead>
            <tbody>
              {rows.slice(0, 20).map((d) => (
                <tr key={d.id}>
                  <Td>
                    <code className="text-[12px]">{d.eventType}</code>
                  </Td>
                  <Td>
                    <StatusBadge status={d.status} />
                  </Td>
                  <Td right>{d.attempts}</Td>
                  <Td muted className="max-w-xs truncate text-[12px]">
                    {d.lastStatusCode ? `HTTP ${d.lastStatusCode}` : ''} {d.lastError ?? ''}
                    {d.status === 'PENDING' && d.attempts > 0 && ` · retry ${relative(d.nextAttemptAt)}`}
                  </Td>
                  <Td muted className="text-[12px]">
                    {dateTime(d.createdAt)}
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )
      }
    </Query>
  );
}

function NewWebhookModal({ open, onClose, eventTypes }: { open: boolean; onClose: () => void; eventTypes: string[] }) {
  const [f, setF] = useState({ url: '', description: '', all: true, events: new Set<string>() });
  const [secret, setSecret] = useState<string | null>(null);
  const create = useAction(() => api<Webhook>('/api/webhooks', { body: { url: f.url, description: f.description || null, events: f.all ? [] : [...f.events] } }), { success: 'Endpoint added' });
  const close = () => {
    onClose();
    setSecret(null);
    setF({ url: '', description: '', all: true, events: new Set() });
    create.reset();
  };
  const groups = new Map<string, string[]>();
  for (const e of eventTypes) {
    const g = e.split('.')[0]!;
    groups.set(g, [...(groups.get(g) ?? []), e]);
  }
  return (
    <Modal
      open={open}
      onClose={close}
      width="max-w-2xl"
      title={secret ? 'Save your signing secret' : 'Add webhook endpoint'}
      footer={
        secret ? (
          <Button variant="primary" onClick={close}>
            Done
          </Button>
        ) : (
          <>
            <Button onClick={close}>Cancel</Button>
            <Button variant="primary" disabled={!/^https?:\/\//.test(f.url) || (!f.all && f.events.size === 0)} loading={create.isPending} onClick={() => create.mutate(undefined, { onSuccess: (w) => setSecret(w.secret ?? '') })}>
              Add endpoint
            </Button>
          </>
        )
      }
    >
      {secret ? (
        <div className="space-y-3">
          <p className="text-[12.5px] text-muted">Use this secret to verify signatures. It is shown once.</p>
          <div className="flex items-center gap-2 rounded-md border border-line bg-sunken p-2">
            <code className="min-w-0 flex-1 truncate text-[12px]">{secret}</code>
            <CopyButton text={secret} />
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          <Field label="Endpoint URL" hint="Must be HTTPS and publicly reachable">
            <Input value={f.url} placeholder="https://example.com/hooks/raremargin" onChange={(e) => setF({ ...f, url: e.target.value })} autoFocus />
          </Field>
          <Field label="Description">
            <Input value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} />
          </Field>
          <Toggle checked={f.all} onChange={(v) => setF({ ...f, all: v })} label="Send all events" description="Including event types added later." />
          {!f.all && (
            <div className="grid max-h-64 grid-cols-2 gap-x-4 gap-y-3 overflow-y-auto rounded-md border border-line p-3">
              {[...groups.entries()].map(([g, list]) => (
                <div key={g}>
                  <div className="mb-1 text-[11.5px] font-semibold tracking-wide text-muted uppercase">{titleCase(g)}</div>
                  {list.map((e) => (
                    <label key={e} className="flex items-center gap-2 py-0.5 text-[12.5px]">
                      <input
                        type="checkbox"
                        className="accent-[var(--color-brand)]"
                        checked={f.events.has(e)}
                        onChange={(ev) => {
                          const next = new Set(f.events);
                          if (ev.target.checked) next.add(e);
                          else next.delete(e);
                          setF({ ...f, events: next });
                        }}
                      />
                      <code className="text-[12px]">{e}</code>
                    </label>
                  ))}
                </div>
              ))}
            </div>
          )}
          {create.error && <ErrorNote error={create.error} />}
        </div>
      )}
    </Modal>
  );
}

// --- Approval workflows -------------------------------------------------------------------------------------------

const SOURCES = ['UI', 'API', 'MCP', 'AI', 'IMPORT'] as const;

function WorkflowsTab() {
  const { can, currency } = useSession();
  const q = useApi<Workflow[]>('/api/approval-workflows');
  const [creating, setCreating] = useState(false);
  const toggle = useAction(({ id, active }: { id: string; active: boolean }) => api(`/api/approval-workflows/${id}`, { method: 'PATCH', body: { active } }), { success: 'Workflow updated' });
  return (
    <>
      <Card
        pad={false}
        title="Approval workflows"
        subtitle="Entries and bills that match a workflow wait in the approvals inbox until someone with the approver role signs off. Anything drafted by the AI assistant or an MCP agent always needs approval."
        actions={
          can('ADMIN') && (
            <Button variant="primary" size="sm" icon={<Icons.Plus size={14} />} onClick={() => setCreating(true)}>
              New workflow
            </Button>
          )
        }
      >
        <Query q={q}>
          {(rows) =>
            rows.length === 0 ? (
              <Empty title="No workflows" />
            ) : (
              <Table>
                <thead>
                  <tr>
                    <Th>Name</Th>
                    <Th>Applies to</Th>
                    <Th>When</Th>
                    <Th>Approver</Th>
                    <Th>Active</Th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((w) => {
                    const c = w.conditions ?? {};
                    const when = [
                      c.minAmount ? `amount ≥ ${money(c.minAmount, currency, { cents: false })}` : null,
                      c.submissionSources?.length ? `from ${c.submissionSources.map((s) => (s === 'UI' ? 'the app' : s)).join(', ')}` : null,
                      c.accountIds?.length ? `${c.accountIds.length} accounts` : null,
                      c.vendorIds?.length ? `${c.vendorIds.length} vendors` : null,
                    ].filter(Boolean);
                    return (
                      <tr key={w.id}>
                        <Td className="font-medium">{w.name}</Td>
                        <Td muted>{w.entityType === 'BILL' ? 'Bills' : 'Journal entries'}</Td>
                        <Td muted className="text-[12.5px]">
                          {when.length ? when.join(' · ') : 'Always'}
                        </Td>
                        <Td>
                          <Badge tone={ROLE_TONE[w.approverRole as Role] ?? 'neutral'}>{titleCase(w.approverRole)}</Badge>
                        </Td>
                        <Td>
                          <button
                            role="switch"
                            aria-checked={w.active}
                            aria-label="Active"
                            disabled={!can('ADMIN')}
                            onClick={() => toggle.mutate({ id: w.id, active: !w.active })}
                            className={cx('relative h-5 w-9 rounded-full transition-colors disabled:opacity-50', w.active ? 'bg-brand' : 'bg-line-strong')}
                          >
                            <span className={cx('absolute top-0.5 left-0.5 size-4 rounded-full bg-white shadow transition-transform', w.active && 'translate-x-4')} />
                          </button>
                        </Td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            )
          }
        </Query>
      </Card>
      <NewWorkflowModal open={creating} onClose={() => setCreating(false)} />
    </>
  );
}

function NewWorkflowModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { currency } = useSession();
  const empty = { name: '', entityType: 'JOURNAL_ENTRY', minAmount: '', sources: new Set<string>(), approverRole: 'ADMIN' };
  const [f, setF] = useState(empty);
  const min = f.minAmount ? parseAmount(f.minAmount, currency) : null;
  const create = useAction(
    () =>
      api('/api/approval-workflows', {
        body: { name: f.name, entityType: f.entityType, approverRole: f.approverRole, conditions: { minAmount: min ?? undefined, submissionSources: f.sources.size ? [...f.sources] : undefined } },
      }),
    { success: 'Workflow created' },
  );
  const close = () => {
    onClose();
    setF(empty);
    create.reset();
  };
  return (
    <Modal
      open={open}
      onClose={close}
      title="New approval workflow"
      footer={
        <>
          <Button onClick={close}>Cancel</Button>
          <Button variant="primary" disabled={!f.name.trim() || (!!f.minAmount && min === null)} loading={create.isPending} onClick={() => create.mutate(undefined, { onSuccess: close })}>
            Create
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Field label="Name">
          <Input value={f.name} placeholder="e.g. Large manual entries" onChange={(e) => setF({ ...f, name: e.target.value })} autoFocus />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Applies to">
            <Select value={f.entityType} onChange={(e) => setF({ ...f, entityType: e.target.value })}>
              <option value="JOURNAL_ENTRY">Journal entries</option>
              <option value="BILL">Bills</option>
            </Select>
          </Field>
          <Field label="Approver role">
            <Select value={f.approverRole} onChange={(e) => setF({ ...f, approverRole: e.target.value })}>
              <option value="ADMIN">Admin or owner</option>
              <option value="OWNER">Owner only</option>
              <option value="ACCOUNTANT">Any accountant</option>
            </Select>
          </Field>
        </div>
        <Field label="Minimum amount" hint="Leave blank to apply at any amount" error={f.minAmount && min === null ? 'Enter an amount' : undefined}>
          <MoneyInput currency={currency} value={f.minAmount} onChange={(e) => setF({ ...f, minAmount: e.target.value })} />
        </Field>
        <Field label="Only when submitted from" hint="Leave all unchecked to apply to every source">
          <div className="flex flex-wrap gap-3 pt-1">
            {SOURCES.map((s) => (
              <label key={s} className="flex items-center gap-1.5 text-[12.5px]">
                <input
                  type="checkbox"
                  className="accent-[var(--color-brand)]"
                  checked={f.sources.has(s)}
                  onChange={(e) => {
                    const next = new Set(f.sources);
                    if (e.target.checked) next.add(s);
                    else next.delete(s);
                    setF({ ...f, sources: next });
                  }}
                />
                <SourceBadge source={s} />
              </label>
            ))}
          </div>
        </Field>
        {create.error && <ErrorNote error={create.error} />}
      </div>
    </Modal>
  );
}

// --- Audit log ----------------------------------------------------------------------------------------------------

const ENTITY_LINK: Record<string, (id: string) => string> = {
  journal_entry: (id) => `/journal/${id}`,
  invoice: (id) => `/ar/invoices/${id}`,
  bill: (id) => `/ap/bills/${id}`,
  customer: (id) => `/ar/customers/${id}`,
  vendor: (id) => `/ap/vendors/${id}`,
  contract: (id) => `/ar/contracts/${id}`,
  account: (id) => `/accounts/${id}`,
};

function AuditTab() {
  const actor = useActorLabel();
  const [entityType, setEntityType] = useState('');
  const [pages, setPages] = useState<number[]>([]);
  const before = pages[pages.length - 1];
  const q = useApi<AuditRow[]>(`/api/audit-log${qs({ entityType, before, limit: 50 })}`);
  useEffect(() => setPages([]), [entityType]);
  return (
    <Card
      pad={false}
      title="Audit log"
      subtitle="Append-only record of every change: who or what made it, from where, and when."
      actions={
        <Select className="w-48" value={entityType} onChange={(e) => setEntityType(e.target.value)}>
          <option value="">All records</option>
          {['journal_entry', 'invoice', 'bill', 'payment', 'contract', 'customer', 'vendor', 'account', 'period', 'bank_transaction', 'approval_request', 'api_key', 'webhook', 'org'].map((t) => (
            <option key={t} value={t}>
              {titleCase(t)}
            </option>
          ))}
        </Select>
      }
      footer={
        <div className="flex items-center justify-between">
          <span className="text-[12px] text-muted">{pages.length ? `Page ${pages.length + 1}` : 'Most recent first'}</span>
          <div className="flex gap-2">
            <Button size="sm" disabled={!pages.length} onClick={() => setPages(pages.slice(0, -1))}>
              Newer
            </Button>
            <Button size="sm" disabled={(q.data?.length ?? 0) < 50} onClick={() => q.data?.length && setPages([...pages, q.data[q.data.length - 1]!.id])}>
              Older
            </Button>
          </div>
        </div>
      }
    >
      <Query q={q}>
        {(rows) =>
          rows.length === 0 ? (
            <Empty title="No events" />
          ) : (
            <Table dense>
              <thead>
                <tr>
                  <Th w="170px">When</Th>
                  <Th>Actor</Th>
                  <Th>Source</Th>
                  <Th>Action</Th>
                  <Th>Record</Th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const href = ENTITY_LINK[r.entityType]?.(r.entityId);
                  return (
                    <tr key={r.id}>
                      <Td muted className="num text-[12px] whitespace-nowrap">
                        {dateTime(r.createdAt)}
                      </Td>
                      <Td>{r.actorType === 'USER' ? actor(r.actorId) : actor(`${r.actorType.toLowerCase()}:${r.actorId}`)}</Td>
                      <Td>
                        <SourceBadge source={r.source} />
                      </Td>
                      <Td>
                        <code className="text-[12px]">{r.action}</code>
                      </Td>
                      <Td muted className="text-[12px]">
                        {href ? (
                          <Link to={href} className="hover:text-brand hover:underline">
                            {titleCase(r.entityType)} {r.entityId.slice(-6)}
                          </Link>
                        ) : (
                          `${titleCase(r.entityType)} ${r.entityId.slice(-6)}`
                        )}
                      </Td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          )
        }
      </Query>
    </Card>
  );
}
